import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { API } from "../lib/api";
import { useAuth, homeFor } from "../context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await API.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser().catch(() => {});
          setStatus("paid");
          return;
        }
      } catch { /* retry */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-4 text-white">
      <div className="glass max-w-md rounded-3xl p-10 text-center" data-testid="payment-success-page">
        {status === "paid" ? (
          <>
            <CheckCircle size={56} weight="fill" className="mx-auto text-neon-green" />
            <h1 className="mt-4 font-display text-2xl font-black">You're in business</h1>
            <p className="mt-2 text-sm text-zinc-400">Subscription activated. Your dashboard is unlocked — time to move the city.</p>
            <Link data-testid="payment-continue-btn" to={homeFor(user)} className="btn-neon mt-6 w-full">Open Dashboard</Link>
          </>
        ) : status === "checking" ? (
          <>
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-neon-gold border-t-transparent" />
            <h1 className="mt-4 font-display text-xl font-black">Confirming payment...</h1>
            <p className="mt-2 text-sm text-zinc-400">Hold tight while Stripe confirms.</p>
          </>
        ) : (
          <>
            <XCircle size={56} weight="fill" className="mx-auto text-neon-yellow" />
            <h1 className="mt-4 font-display text-xl font-black">Still processing</h1>
            <p className="mt-2 text-sm text-zinc-400">Payment is being confirmed by Stripe. Check your dashboard in a moment — it activates automatically.</p>
            <Link data-testid="payment-continue-btn" to={homeFor(user)} className="btn-ghost-neon mt-6 w-full">Continue</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-4 text-white">
      <div className="glass max-w-md rounded-3xl p-10 text-center" data-testid="payment-cancel-page">
        <XCircle size={56} weight="fill" className="mx-auto text-zinc-500" />
        <h1 className="mt-4 font-display text-xl font-black">Checkout canceled</h1>
        <p className="mt-2 text-sm text-zinc-400">No charge was made. Your account is still active — upgrade whenever you're ready.</p>
        <Link data-testid="payment-back-btn" to="/app/profile" className="btn-ghost-neon mt-6 w-full">Back to Profile</Link>
      </div>
    </div>
  );
}
